import React, { useMemo } from 'react';
import { truncate } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import { deepTrim } from '@soundxyz/utils/string';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { type FragmentType, getFragment, PostRepostedInfoFragmentDoc } from '@graphql/reactQuery';
import { useLexicalValidateGetText } from '@hooks/useLexicalValidate';
import { SPACE } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { FeedActivityContainer, FeedListUsername } from '../../components/new-shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PostRepostedInfo on PostReposted {
    id
    date
    repost {
      __typename
      id
      creator {
        id
        username
        webappUri
        publicAddress
        avatar {
          id
          url
        }
        artist {
          id
        }
      }
    }
    originalPost {
      __typename
      id
      webappUri
      lexicalContent
      content
      creator {
        id
        username
        webappUri
        artist {
          id
        }
      }

      ... on ReleasePost {
        ...ListReleaseCreatedPost
      }
      ... on PlaylistPost {
        ...ListPlaylistCreatedPost
      }
    }
  }
`);

export const PostReposted = ({
  repostFrag,
}: {
  repostFrag: FragmentType<PostRepostedInfoFragmentDoc>;
}) => {
  const repostData = getFragment(PostRepostedInfoFragmentDoc, repostFrag);

  const repost = repostData.repost;
  const originalPost = repostData.originalPost;

  const lexicalContent = useLexicalValidateGetText(originalPost?.lexicalContent);

  const trimmedContent = useMemo(() => {
    return originalPost?.lexicalContent && lexicalContent
      ? deepTrim(lexicalContent)
      : originalPost?.content
      ? deepTrim(originalPost.content)
      : null;
  }, [originalPost, lexicalContent]);

  const truncatedContent = useMemo(() => {
    if (trimmedContent) return truncate(trimmedContent, { length: 20 });

    return null;
  }, [trimmedContent]);

  /**
   * These cases are filtered out on the query level
   */
  if (!repost || !originalPost) return null;

  const contentText = lexicalContent || originalPost.content;

  const didTruncateContent =
    contentText.length !== trimmedContent?.length || trimmedContent.length > 20;

  const repostContentTrigger = (
    <span className="font-base font-semibold text-base800">"{truncatedContent}"</span>
  );

  return (
    <AccessibleLink pathnameUri={webappUriPath(originalPost.webappUri)}>
      <FeedActivityContainer timestamp={repostData.date}>
        <div className="flex w-full items-center">
          <div className="flex gap-2">
            <div className="hidden h-12 w-12 flex-shrink-0 md:flex">
              <Avatar
                disableLink
                size={48}
                borderRadius={999}
                walletAddress={repost.creator.publicAddress}
                webappUri={repost.creator.webappUri}
                src={repost.creator.avatar?.url}
                username={repost.creator.username}
              />
            </div>
            <div className="h-10 w-10 flex-shrink-0 md:hidden">
              <Avatar
                disableLink
                size={40}
                borderRadius={999}
                walletAddress={repost.creator.publicAddress}
                webappUri={repost.creator.webappUri}
                src={repost.creator.avatar?.url}
                username={repost.creator.username}
              />
            </div>
            <div className="hidden items-center md:inline-flex">
              <FeedListUsername
                username={repost.creator.username}
                isArtist={!!repost.creator.artist}
              />
            </div>
          </div>

          <div className="ml-2 flex-wrap items-start md:ml-0 md:items-center">
            <div className="inline-flex md:hidden">
              <FeedListUsername
                username={repost.creator.username}
                isArtist={!!repost.creator.artist}
              />
            </div>
            <span className="w-full font-base text-neutral500">
              reposted{SPACE}
              {trimmedContent ? (
                <>
                  {didTruncateContent ? (
                    <Tooltip openOnClick={false}>
                      <Tooltip.Trigger asChild className="cursor-pointer">
                        {repostContentTrigger}
                      </Tooltip.Trigger>
                      <Tooltip.Content className="whitespace-pre-wrap rounded-[6px] bg-neutral900 px-2.5 py-2 font-base text-base-s font-medium text-white">
                        {contentText}
                      </Tooltip.Content>
                    </Tooltip>
                  ) : (
                    repostContentTrigger
                  )}
                  {SPACE}
                </>
              ) : null}
              from{SPACE}
              <FeedListUsername
                username={originalPost.creator.username}
                isArtist={!!originalPost.creator.artist}
              />
            </span>
          </div>
        </div>
      </FeedActivityContainer>
    </AccessibleLink>
  );
};
